// better-auth's sign-up, sign-in and session endpoints — `/v1/auth/*` (spec/07 §2.1, §3.1;
// spec/08 §4.1; F-IAM-01, F-IAM-02; issue #412).
//
// These endpoints are **better-auth's**, served by its handler at `/v1/auth/*`; nothing here mounts
// them. They are declared so that the api's OpenAPI document, the TypeScript client and the C#
// station DTOs describe them like every other endpoint (spec/08 §1.1), with the second-factor ones
// beside them in `two-factor.ts`.
//
// **Declared, not copied from better-auth's own document.** The `openAPI` plugin generates one, and
// it is the reference these are checked against (`apps/server`'s auth contract suite): every path,
// method, `operationId` and request field here must be one better-auth 1.7.5 serves, so an upgrade
// that renames one fails CI rather than a client. It is not the source, because it does not describe
// this api:
//
//   * It knows nothing of our hooks: the `consent` a sign-up must carry (F-CMP-06), the alternatives a
//     taken gamer tag comes back with, the fields `/phone-number/verify` refuses, the per-account
//     lock's `RATE_LIMITED`.
//   * Some of its responses are not what the endpoint returns — `/update-user` answers
//     `{ status }`, not `{ user }`; a password sign-in that stops at the second factor answers
//     `{ twoFactorRedirect }`, which it does not mention. The responses here are checked against
//     real answers instead, one request per route.
//   * It is OpenAPI 3.0 in places (`nullable`), half its operations have no `operationId`, and its
//     components are better-auth's tables — `Account` with its password hash, `Jwks` with its
//     private key.
//
// **Which endpoints.** The ones a client of a shipped feature calls: phone sign-in and sign-up
// (F-IAM-01), email or username and password, email codes and gamer tags (F-IAM-02), Google, Discord
// and Steam (F-IAM-03 … F-IAM-05) with the callbacks their providers redirect to, linking one from a
// session, listing them and unlinking one, the session and signing out. better-auth serves more — passkeys, the
// admin plugin, API keys, its session list — and each joins the contract with the feature whose
// client calls it; the auth suite names every one that is still outside it, so an upgrade that adds
// an endpoint is noticed. The two Steam endpoints are our own better-auth plugin's (spec/07 §3.4).
//
// **Identifiers are better-auth's**: a user id and a session id are UUIDs, not TypeIDs.
//
// **Failures are better-auth's shape, `{ code, message }`**, not problem+json — ours in front of and
// inside better-auth use the same shape, so a client handles both alike. `code` is stable and upper
// snake case; `message` is for a person.
import { createRoute } from '@hono/zod-openapi';
import { z } from "../zod.js";
import { JSON_CONTENT_TYPE, jsonResponse } from "../openapi/responses.js";
import { SignUpConsentSchema } from "../primitives/consent.js";
import { OtpCodeSchema } from "../primitives/otp.js";
import { PhoneNumberSchema } from "../primitives/phone-number.js";
import { IsoDateTimeSchema } from "../primitives/scalars.js";
import { UsernameSchema } from "../primitives/username.js";
import { AuthErrorSchema, BANNED_USER_REFUSAL } from "./two-factor.js";
const IDENTITY = ['identity'];
/** better-auth's own password limits: at least eight (spec/07 §2), at most its default 128. */
const PASSWORD = z.string().min(8).max(128);
const SESSION = 'Needs a session: `Authorization: Bearer <token>` in the apps, the session cookie in a browser.';
/** A refusal in better-auth's shape, naming the codes a client should expect. */
function authError(description, ...codes) {
    const suffix = codes.length > 0 ? ` Codes: ${codes.join(', ')}.` : '';
    return { description: `${description}${suffix}`, content: { [JSON_CONTENT_TYPE]: { schema: AuthErrorSchema } } };
}
/** The per-IP limit better-auth keeps and the per-account and per-number limits we keep (spec/07 §2.2). */
const RATE_LIMITED = authError('Too many attempts. `Retry-After` says when to try again.', 'RATE_LIMITED');
const json = (schema) => ({
    body: { content: { [JSON_CONTENT_TYPE]: { schema } }, required: true },
});
// -------------------------------------------------------------------------------------------------
// Shapes
// -------------------------------------------------------------------------------------------------
export const AuthUserSchema = z
    .object({
    /** better-auth's user id: a UUID. */
    id: z.string(),
    /** Empty for an account made by phone until the player gives a name (spec/07 §3.1). */
    name: z.string(),
    /**
     * The account's email. An account made by phone has a synthetic `…@phone.invalid` address, which
     * is never mailed and never accepted where an email is typed (spec/07 §3.1).
     */
    email: z.string(),
    emailVerified: z.boolean(),
    image: z.string().nullable().optional(),
    /** E.164, when the account has a phone. */
    phoneNumber: z.string().nullable().optional(),
    phoneNumberVerified: z.boolean().nullable().optional(),
    /** The gamer tag, lower-cased: what a username sign-in matches. */
    username: z.string().nullable().optional(),
    /** The gamer tag as the player typed it: what is shown. */
    displayUsername: z.string().nullable().optional(),
    twoFactorEnabled: z.boolean().nullable().optional(),
    createdAt: IsoDateTimeSchema,
    updatedAt: IsoDateTimeSchema,
})
    .openapi('AuthUser', { description: 'The signed-in person, as better-auth returns them.' });
export const AuthSessionSchema = z
    .object({
    /** better-auth's session id: a UUID. */
    id: z.string(),
    /** The session token — what the apps send as `Authorization: Bearer`. */
    token: z.string(),
    userId: z.string(),
    expiresAt: IsoDateTimeSchema,
    createdAt: IsoDateTimeSchema,
    updatedAt: IsoDateTimeSchema,
    ipAddress: z.string().nullable().optional(),
    userAgent: z.string().nullable().optional(),
    /** A console's default organisation, never the scope of a request (spec/07 §6.1). */
    activeOrganizationId: z.string().nullable().optional(),
    /** Set when HQ is impersonating the user under break-glass (spec/07 §8). */
    impersonatedBy: z.string().nullable().optional(),
    /**
     * How the session proved a second factor, when it did — `totp`, `backup_code` or `passkey` — which
     * is what the console gate reads (spec/07 §6.6). A string rather than an enum: a method added later
     * must not break a client that only shows it.
     */
    secondFactor: z.string().nullable().optional(),
    secondFactorAt: IsoDateTimeSchema.nullable().optional(),
})
    .openapi('AuthSession', { description: 'A better-auth session.' });
export const AuthSessionStateSchema = z
    .object({ session: AuthSessionSchema, user: AuthUserSchema })
    .nullable()
    .openapi('AuthSessionState', { description: 'The caller’s session and who it belongs to, or `null` when not signed in.' });
export const AuthErrorWithSuggestionsSchema = z
    .object({
    code: z.string(),
    message: z.string(),
    /** With `USERNAME_IS_ALREADY_TAKEN`: up to three free gamer tags close to the one asked for (spec/12 §3.3). */
    suggestions: z.array(UsernameSchema).optional(),
})
    .openapi('AuthErrorWithSuggestions', {
    description: 'A refusal from the authentication endpoints; a taken gamer tag carries alternatives.',
});
export const AuthSuccessSchema = z.object({ success: z.boolean() }).openapi('AuthSuccess');
export const AuthStatusSchema = z.object({ status: z.boolean() }).openapi('AuthStatus');
export const AuthMessageSchema = z.object({ message: z.string() }).openapi('AuthMessage');
// -------------------------------------------------------------------------------------------------
// Signing up and signing in with an email or a gamer tag (F-IAM-02)
// -------------------------------------------------------------------------------------------------
export const SignUpWithEmailRequestSchema = z
    .object({
    name: z.string(),
    email: z.email(),
    password: PASSWORD,
    /** A gamer tag to take at once. Taken, it is refused with alternatives. */
    username: UsernameSchema.optional(),
    /** Terms and privacy, required: an account is never created without them (F-CMP-06). */
    consent: SignUpConsentSchema,
    /** `false` for a session that ends with the browser. */
    rememberMe: z.boolean().optional(),
})
    .openapi('SignUpWithEmailRequest');
export const SignedUpSchema = z
    .object({
    /** Always `null` here: the address must be verified by its code before the account signs in. */
    token: z.string().nullable(),
    user: AuthUserSchema,
})
    .openapi('SignedUp', {
    description: 'Accepted. The same answer for an address that already has an account, so the answer says nothing about ' +
        'which addresses exist.',
});
export const signUpWithEmailRoute = createRoute({
    method: 'post',
    path: '/v1/auth/sign-up/email',
    operationId: 'signUpWithEmailAndPassword',
    tags: IDENTITY,
    summary: 'Create an account with an email address and a password',
    description: 'Needs the terms and privacy accepted in `consent`. The account cannot sign in until its address is verified ' +
        'with the code sent to it (`/email-otp/verify-email`). The password is checked against breached-password lists.',
    request: json(SignUpWithEmailRequestSchema),
    responses: {
        200: jsonResponse(SignedUpSchema, 'Accepted.'),
        400: {
            description: 'Refused. Codes: CONSENT_REQUIRED, INVALID_EMAIL, PASSWORD_TOO_SHORT, PASSWORD_TOO_LONG, PASSWORD_COMPROMISED, ' +
                'USERNAME_IS_ALREADY_TAKEN (with suggestions), USERNAME_TOO_SHORT, USERNAME_TOO_LONG, INVALID_USERNAME.',
            content: { [JSON_CONTENT_TYPE]: { schema: AuthErrorWithSuggestionsSchema } },
        },
        409: authError('The consent names a document version that is not the one in force.', 'CONSENT_VERSION_OUTDATED'),
        429: RATE_LIMITED,
    },
});
export const SignInWithEmailRequestSchema = z
    .object({
    email: z.email(),
    password: z.string().min(1).max(128),
    rememberMe: z.boolean().optional(),
})
    .openapi('SignInWithEmailRequest');
export const SignInWithUsernameRequestSchema = z
    .object({
    /** The gamer tag, in any capitalisation. */
    username: z.string().min(1).max(64),
    password: z.string().min(1).max(128),
    rememberMe: z.boolean().optional(),
})
    .openapi('SignInWithUsernameRequest');
export const PasswordSignInSchema = z
    .object({
    /** The session token: send it as `Authorization: Bearer` from the apps. Absent when `twoFactorRedirect` is set. */
    token: z.string().optional(),
    user: AuthUserSchema.optional(),
    redirect: z.boolean().optional(),
    url: z.string().nullable().optional(),
    /**
     * `true` when the account has a second factor: there is no session yet, and the sign-in finishes at
     * `/two-factor/verify-totp` or `/two-factor/verify-backup-code` with the `two_factor` cookie set here.
     */
    twoFactorRedirect: z.boolean().optional(),
    /** With `twoFactorRedirect`: the factors the account can finish with. */
    twoFactorMethods: z.array(z.string()).optional(),
})
    .openapi('PasswordSignIn', {
    description: 'Either a session (`token` and `user`), or — for an account with a second factor — `twoFactorRedirect`.',
});
const PASSWORD_SIGN_IN_FAILURES = {
    401: authError('The password is wrong, or there is no such account — the same answer, at the same time, either way.', 'INVALID_EMAIL_OR_PASSWORD', 'INVALID_USERNAME_OR_PASSWORD'),
    403: authError('The right password, for an account with no verified email or phone to sign in by; or an account banned from ' +
        'the Platform, which is refused every session and never told why (spec/07 §3.5).', 'EMAIL_NOT_VERIFIED', 'BANNED_USER'),
    429: authError('Too many attempts: ten a minute from one address, or five failures in fifteen minutes on one account, which then ' +
        'locks it for fifteen minutes. `Retry-After` says when to try again.', 'RATE_LIMITED'),
};
export const signInWithEmailRoute = createRoute({
    method: 'post',
    path: '/v1/auth/sign-in/email',
    operationId: 'signInEmail',
    tags: IDENTITY,
    summary: 'Sign in with an email address and a password',
    description: 'The address must be verified. An account with a second factor stops at `twoFactorRedirect`.',
    request: json(SignInWithEmailRequestSchema),
    responses: {
        200: jsonResponse(PasswordSignInSchema, 'Signed in, or waiting for the second factor.'),
        ...PASSWORD_SIGN_IN_FAILURES,
    },
});
export const signInWithUsernameRoute = createRoute({
    method: 'post',
    path: '/v1/auth/sign-in/username',
    operationId: 'signInUsername',
    tags: IDENTITY,
    summary: 'Sign in with a gamer tag and a password',
    description: 'The account needs a verified email or a verified phone, so a phone-first player who has added a password can ' +
        'use it. An account with a second factor stops at `twoFactorRedirect`.',
    request: json(SignInWithUsernameRequestSchema),
    responses: {
        200: jsonResponse(PasswordSignInSchema, 'Signed in, or waiting for the second factor.'),
        ...PASSWORD_SIGN_IN_FAILURES,
    },
});
// -------------------------------------------------------------------------------------------------
// Email codes (F-IAM-02): verifying an address, passwordless sign-in on the web, a password reset
// -------------------------------------------------------------------------------------------------
export const EMAIL_CODE_PURPOSES = ['email-verification', 'sign-in', 'forget-password'];
export const SendEmailCodeRequestSchema = z
    .object({
    email: z.email(),
    /** What the code is for. */
    type: z.enum(EMAIL_CODE_PURPOSES),
})
    .openapi('SendEmailCodeRequest');
export const sendEmailCodeRoute = createRoute({
    method: 'post',
    path: '/v1/auth/email-otp/send-verification-otp',
    operationId: 'sendEmailVerificationOTP',
    tags: IDENTITY,
    summary: 'Email a one-time code',
    description: 'A 6-digit code, valid for 10 minutes, five attempts. The answer is the same whether or not the address has an ' +
        'account, and whether or not the code could be sent; a synthetic `.invalid` address is refused.',
    request: json(SendEmailCodeRequestSchema),
    responses: {
        200: jsonResponse(AuthSuccessSchema, 'Accepted.'),
        400: authError('The address cannot be used.', 'INVALID_EMAIL'),
        429: RATE_LIMITED,
    },
});
export const EmailCodeRequestSchema = z
    .object({ email: z.email(), otp: OtpCodeSchema })
    .openapi('EmailCodeRequest', { description: 'An address and the code emailed to it.' });
export const EmailVerifiedSchema = z
    .object({
    status: z.boolean(),
    /** `null`: verifying an address does not sign in. Sign in with the password afterwards. */
    token: z.string().nullable(),
    user: AuthUserSchema,
})
    .openapi('EmailVerified');
export const verifyEmailWithCodeRoute = createRoute({
    method: 'post',
    path: '/v1/auth/email-otp/verify-email',
    operationId: 'verifyEmailOTP',
    tags: IDENTITY,
    summary: 'Verify an email address with the code sent to it',
    request: json(EmailCodeRequestSchema),
    responses: {
        200: jsonResponse(EmailVerifiedSchema, 'Verified.'),
        400: authError('The code is wrong, expired or used up.', 'INVALID_OTP', 'OTP_EXPIRED', 'TOO_MANY_ATTEMPTS', 'INVALID_EMAIL'),
        429: RATE_LIMITED,
    },
});
export const SignedInSchema = z
    .object({
    /** The session token: send it as `Authorization: Bearer` from the apps. */
    token: z.string(),
    user: AuthUserSchema,
})
    .openapi('SignedIn');
export const SignInWithEmailCodeRequestSchema = z
    .object({
    email: z.email(),
    otp: OtpCodeSchema,
    /**
     * Terms, privacy and marketing (F-CMP-06), required when this code creates the account (F-KIO-04, ADR-0030):
     * a new address without it is answered `CONSENT_REQUIRED` and **keeps its code**, so the client shows the
     * consents and sends the same code again with them. Ignored for an address that is already an account.
     */
    consent: SignUpConsentSchema.optional(),
})
    .strict()
    .openapi('SignInWithEmailCodeRequest', { description: 'An address, the code emailed to it, and — for a new account — the consent.' });
export const signInWithEmailCodeRoute = createRoute({
    method: 'post',
    path: '/v1/auth/sign-in/email-otp',
    operationId: 'signInWithEmailOTP',
    tags: IDENTITY,
    summary: 'Sign in, or sign up, with a code emailed to the address',
    description: 'Passwordless sign-in on the web and in the apps. It signs an existing account in, and — since F-KIO-04 ' +
        '(ADR-0030) — creates the account for a new address, only with `consent`, the address verified by the code. ' +
        'The server assigns the gamer tag; the player changes it later. Codes to one address are capped at 3 per 10 ' +
        'minutes and 10 a day.',
    request: json(SignInWithEmailCodeRequestSchema),
    responses: {
        200: jsonResponse(SignedInSchema, 'Signed in.'),
        400: authError('The code is wrong, expired or used up; a new address was sent without the consent; or a field was sent that is not accepted here.', 'INVALID_OTP', 'OTP_EXPIRED', 'TOO_MANY_ATTEMPTS', 'INVALID_EMAIL', 'CONSENT_REQUIRED', 'UNEXPECTED_FIELDS'),
        403: BANNED_USER_REFUSAL,
        409: authError('The consent names a document version that is not the one in force.', 'CONSENT_VERSION_OUTDATED'),
        429: RATE_LIMITED,
    },
});
export const EmailPasswordResetRequestSchema = z.object({ email: z.email() }).openapi('EmailPasswordResetRequest');
export const requestPasswordResetByEmailRoute = createRoute({
    method: 'post',
    path: '/v1/auth/email-otp/request-password-reset',
    operationId: 'requestPasswordResetWithEmailOTP',
    tags: IDENTITY,
    summary: 'Email a code to reset the password',
    description: 'The same answer, taking no less than 500 ms, whether or not the address has an account (spec/07 §2.2); at most ' +
        'three codes an hour reach one account.',
    request: json(EmailPasswordResetRequestSchema),
    responses: {
        200: jsonResponse(AuthSuccessSchema, 'Accepted.'),
        400: authError('The address cannot be used.', 'INVALID_EMAIL'),
        429: RATE_LIMITED,
    },
});
export const ResetPasswordWithEmailCodeRequestSchema = z
    .object({ email: z.email(), otp: OtpCodeSchema, password: PASSWORD })
    .openapi('ResetPasswordWithEmailCodeRequest');
export const resetPasswordWithEmailCodeRoute = createRoute({
    method: 'post',
    path: '/v1/auth/email-otp/reset-password',
    operationId: 'resetPasswordWithEmailOTP',
    tags: IDENTITY,
    summary: 'Set a new password with the code emailed to the address',
    description: 'The new password is checked against breached-password lists.',
    request: json(ResetPasswordWithEmailCodeRequestSchema),
    responses: {
        200: jsonResponse(AuthSuccessSchema, 'The password is changed.'),
        400: authError('The code is wrong, expired or used up, or the password is refused.', 'INVALID_OTP', 'OTP_EXPIRED', 'TOO_MANY_ATTEMPTS', 'PASSWORD_TOO_SHORT', 'PASSWORD_TOO_LONG', 'PASSWORD_COMPROMISED'),
        429: RATE_LIMITED,
    },
});
// -------------------------------------------------------------------------------------------------
// Phone codes (F-IAM-01): signing in and up, and a password reset — which is how a phone account adds one
// -------------------------------------------------------------------------------------------------
/**
 * `auth.phoneSignIn` off (spec/07 §3.1; the owner's instruction of 2026-10-08, and the default since): every
 * endpoint below is refused before anything is looked up, the same for every number, and no code is sent.
 */
const PHONE_SIGN_IN_OFF = authError('Phone sign-in is turned off on the Platform (`auth.phoneSignIn`; `GET /v1/meta` says whether it is on). Nothing ' +
    'was looked up or sent.', 'PHONE_SIGN_IN_OFF');
export const PhoneCodeRequestSchema = z.object({ phoneNumber: PhoneNumberSchema }).openapi('PhoneCodeRequest');
export const sendPhoneCodeRoute = createRoute({
    method: 'post',
    path: '/v1/auth/phone-number/send-otp',
    operationId: 'sendPhoneNumberOTP',
    tags: IDENTITY,
    summary: 'Send a sign-in code to a phone',
    description: 'A 6-digit code, valid for 10 minutes, by SMS or WhatsApp (`auth.otpChannel`). A kiosk never calls this: it uses ' +
        '`/v1/station/sign-in/phone/code`. A 503 means no code was sent — never that one was.',
    request: json(PhoneCodeRequestSchema),
    responses: {
        200: jsonResponse(AuthMessageSchema, 'The code has been handed to a provider.'),
        400: authError('The number cannot be used.', 'INVALID_PHONE_NUMBER', 'PREMIUM_RATE_NUMBER'),
        403: PHONE_SIGN_IN_OFF,
        429: authError('Too many codes: three per ten minutes and ten a day for one number, twenty an hour from one address, or the ' +
            'account is locked. `Retry-After` says when to try again.', 'RATE_LIMITED'),
        503: authError('No provider could send the code; nothing was sent.', 'OTP_DELIVERY_UNAVAILABLE'),
    },
});
export const VerifyPhoneNumberRequestSchema = z
    .object({
    phoneNumber: PhoneNumberSchema,
    code: OtpCodeSchema,
    /** `true` to verify the number without signing in. */
    disableSession: z.boolean().optional(),
    /**
     * Terms, privacy and marketing (F-CMP-06). Required when this code creates the account: a new number
     * without it is answered `CONSENT_REQUIRED` and **keeps its code**, so the client shows the consents and
     * sends the same code again with them. Ignored for an account that already exists.
     */
    consent: SignUpConsentSchema.optional(),
})
    .openapi('VerifyPhoneNumberRequest', { description: 'Nothing else is accepted: an unexpected field is refused.' });
export const PhoneVerifiedSchema = z
    .object({
    status: z.boolean(),
    /** The session token; `null` with `disableSession`. */
    token: z.string().nullable(),
    user: AuthUserSchema,
})
    .openapi('PhoneVerified', { description: 'Signed in — with the account created on the first verification of a new number.' });
export const verifyPhoneNumberRoute = createRoute({
    method: 'post',
    path: '/v1/auth/phone-number/verify',
    operationId: 'verifyPhoneNumber',
    tags: IDENTITY,
    summary: 'Sign in, or sign up, with the code sent to a phone',
    description: 'Creates the account on the first verification of a new number — only with the terms and privacy accepted in ' +
        '`consent`. Five wrong codes void the code; five failures in fifteen minutes lock the account for fifteen.',
    request: json(VerifyPhoneNumberRequestSchema),
    responses: {
        200: jsonResponse(PhoneVerifiedSchema, 'Signed in.'),
        400: authError('The code is wrong, expired or used up, a field is not accepted, or a new account was asked for without the ' +
            'terms and privacy.', 'INVALID_OTP', 'OTP_EXPIRED', 'OTP_NOT_FOUND', 'TOO_MANY_ATTEMPTS', 'INVALID_PHONE_NUMBER', 'UNEXPECTED_FIELDS', 'CONSENT_REQUIRED'),
        403: authError('Banned from the Platform: the code was right, and no session is created ("This account cannot sign in. Please ' +
            'contact support." — never the reason). Or phone sign-in is turned off on the Platform (`auth.phoneSignIn`): ' +
            'nothing was looked up or checked.', 'BANNED_USER', 'PHONE_SIGN_IN_OFF'),
        409: authError('The consent names a document version that is not the one in force.', 'CONSENT_VERSION_OUTDATED'),
        429: authError('Five failures in fifteen minutes on this account, which is locked for fifteen. `Retry-After` says when to try again.', 'RATE_LIMITED'),
    },
});
export const requestPasswordResetByPhoneRoute = createRoute({
    method: 'post',
    path: '/v1/auth/phone-number/request-password-reset',
    operationId: 'requestPasswordResetPhoneNumber',
    tags: IDENTITY,
    summary: 'Send a code to set or reset the password',
    description: 'Also how a phone-first player **adds** a password. The same answer, taking no less than 500 ms, whether or not the ' +
        'number has an account; at most three codes an hour reach one account.',
    request: json(PhoneCodeRequestSchema),
    responses: {
        200: jsonResponse(AuthStatusSchema, 'Accepted.'),
        403: PHONE_SIGN_IN_OFF,
        429: RATE_LIMITED,
    },
});
export const ResetPasswordWithPhoneCodeRequestSchema = z
    .object({ phoneNumber: PhoneNumberSchema, otp: OtpCodeSchema, newPassword: PASSWORD })
    .openapi('ResetPasswordWithPhoneCodeRequest');
export const resetPasswordWithPhoneCodeRoute = createRoute({
    method: 'post',
    path: '/v1/auth/phone-number/reset-password',
    operationId: 'resetPasswordPhoneNumber',
    tags: IDENTITY,
    summary: 'Set a new password with the code sent to the phone',
    description: 'Creates the password when the account has none. The new password is checked against breached-password lists.',
    request: json(ResetPasswordWithPhoneCodeRequestSchema),
    responses: {
        200: jsonResponse(AuthStatusSchema, 'The password is set.'),
        400: authError('The code is wrong, expired or used up, or the password is refused.', 'INVALID_OTP', 'OTP_EXPIRED', 'OTP_NOT_FOUND', 'TOO_MANY_ATTEMPTS', 'PASSWORD_TOO_SHORT', 'PASSWORD_TOO_LONG', 'PASSWORD_COMPROMISED'),
        403: PHONE_SIGN_IN_OFF,
        429: RATE_LIMITED,
    },
});
// -------------------------------------------------------------------------------------------------
// Gamer tags (F-IAM-02)
// -------------------------------------------------------------------------------------------------
export const UsernameAvailabilityRequestSchema = z
    .object({ username: z.string().min(1).max(64) })
    .openapi('UsernameAvailabilityRequest');
export const UsernameAvailabilitySchema = z
    .object({
    available: z.boolean(),
    /** When it is taken: up to three free tags close to it (spec/12 §3.3). */
    suggestions: z.array(UsernameSchema).optional(),
})
    .openapi('UsernameAvailability');
export const checkUsernameAvailabilityRoute = createRoute({
    method: 'post',
    path: '/v1/auth/is-username-available',
    operationId: 'isUsernameAvailable',
    tags: IDENTITY,
    summary: 'Is this gamer tag free?',
    description: 'Unique ignoring case. A taken tag comes back with alternatives.',
    request: json(UsernameAvailabilityRequestSchema),
    responses: {
        200: jsonResponse(UsernameAvailabilitySchema, 'Whether it is free.'),
        422: authError('Not a gamer tag: 3–20 letters, digits, "_" or ".".', 'USERNAME_TOO_SHORT', 'USERNAME_TOO_LONG', 'INVALID_USERNAME'),
        429: RATE_LIMITED,
    },
});
export const UpdateUserRequestSchema = z
    .object({
    /** The gamer tag, as it should be shown. Changing only its capitalisation keeps it. */
    username: UsernameSchema,
})
    .openapi('UpdateUserRequest');
export const updateUserRoute = createRoute({
    method: 'post',
    path: '/v1/auth/update-user',
    operationId: 'updateUser',
    tags: IDENTITY,
    summary: 'Choose a gamer tag',
    description: `${SESSION} A tag somebody else holds, in any capitalisation, is refused with alternatives.`,
    request: json(UpdateUserRequestSchema),
    responses: {
        200: jsonResponse(AuthStatusSchema, 'Chosen.'),
        400: {
            description: 'Refused. Codes: USERNAME_IS_ALREADY_TAKEN (with suggestions), USERNAME_TOO_SHORT, USERNAME_TOO_LONG, ' +
                'INVALID_USERNAME, DISPLAY_USERNAME_NOT_ACCEPTED.',
            content: { [JSON_CONTENT_TYPE]: { schema: AuthErrorWithSuggestionsSchema } },
        },
        401: authError('Not signed in.', 'UNAUTHORIZED'),
        429: RATE_LIMITED,
    },
});
// -------------------------------------------------------------------------------------------------
// The session
// -------------------------------------------------------------------------------------------------
export const getSessionRoute = createRoute({
    method: 'get',
    path: '/v1/auth/get-session',
    operationId: 'getSession',
    tags: IDENTITY,
    summary: 'The caller’s session',
    description: `${SESSION} Answers \`null\`, not 401, when there is none.`,
    responses: {
        200: jsonResponse(AuthSessionStateSchema, 'The session and its user, or `null`.'),
        429: RATE_LIMITED,
    },
});
export const signOutRoute = createRoute({
    method: 'post',
    path: '/v1/auth/sign-out',
    operationId: 'signOut',
    tags: IDENTITY,
    summary: 'Sign out',
    description: 'Ends the caller’s session. Answers the same when there is none.',
    responses: {
        200: jsonResponse(AuthSuccessSchema, 'Signed out.'),
        429: RATE_LIMITED,
    },
});
// -------------------------------------------------------------------------------------------------
// Google, Discord and Steam (F-IAM-03, F-IAM-04, F-IAM-05), with the consent a sign-up needs
// (F-CMP-06, issue #418). A kiosk never starts these: it reaches
// them only through the station sign-in handoff (ADR-0051), finished in a real browser or on a phone.
// -------------------------------------------------------------------------------------------------
/**
 * The providers `/sign-in/social` serves. Steam speaks OpenID 2.0 and has its own two endpoints. A
 * provider the server has not been configured for answers `PROVIDER_NOT_FOUND`.
 */
export const SOCIAL_SIGN_IN_PROVIDERS = ['google', 'discord'];
export const SocialSignInProviderSchema = z
    .enum(SOCIAL_SIGN_IN_PROVIDERS)
    .openapi('SocialSignInProvider', { description: 'An OAuth provider a player may sign in with.' });
/**
 * Every provider a person may sign in with and link to their account: the two OAuth ones and Steam, which
 * has its own endpoints. `GET /v1/meta`'s `signIn.social` names the ones this deployment has switched on.
 */
export const SOCIAL_PROVIDERS = [...SOCIAL_SIGN_IN_PROVIDERS, 'steam'];
export const SocialProviderSchema = z
    .enum(SOCIAL_PROVIDERS)
    .openapi('SocialProvider', { description: 'Google, Discord or Steam: a provider a person may sign in with and link.' });
const CALLBACK_URL = z
    .string()
    .optional()
    .openapi({
    description: 'Where the browser lands, signed in: a page of an origin the api trusts (the player web), or a path.',
});
const ERROR_CALLBACK_URL = z
    .string()
    .optional()
    .openapi({
    description: 'Where the browser lands when the sign-in is refused, with `?error=<code>`: `CONSENT_REQUIRED` for a new ' +
        'player who sent no `consent` (show the consents and start again with them), `account_not_linked` for an ' +
        'address that already has an account (sign in, then link), `BANNED_USER`, `state_mismatch`, `access_denied`, ' +
        '`invalid_assertion` (Steam), `account_already_linked_to_different_user` (a link).',
});
const NEW_USER_CALLBACK_URL = z
    .string()
    .optional()
    .openapi({ description: 'Where a sign-in that created the account lands, when that differs from `callbackURL`.' });
const SIGN_UP_CONSENT = SignUpConsentSchema.optional().openapi({
    description: 'Terms, privacy and marketing (F-CMP-06), for a player who may be new: kept with the flow — never in a URL — ' +
        'and written with the account if the provider round trip creates one. Ignored for a player who has an ' +
        'account. Without it, a round trip that would create an account is refused with `CONSENT_REQUIRED`.',
});
export const GoogleIdTokenSchema = z
    .object({
    /** The ID token the Google sign-in SDK handed the app, requested for the api's (web) client id. */
    token: z.string().min(1),
    /** The nonce the app asked Google to put in the token, when it asked for one. */
    nonce: z.string().optional(),
})
    .openapi('GoogleIdToken', { description: 'A Google ID token from the native sign-in SDK in the phone apps.' });
export const SocialSignInRequestSchema = z
    .object({
    provider: SocialSignInProviderSchema,
    callbackURL: CALLBACK_URL,
    errorCallbackURL: ERROR_CALLBACK_URL,
    newUserCallbackURL: NEW_USER_CALLBACK_URL,
    /** `true` to take the provider's URL from the answer rather than follow a `Location` header. */
    disableRedirect: z.boolean().optional(),
    /**
     * The phone apps' Google sign-in: the account is signed in, or created, in this request and a session
     * comes back. Google only; nothing else is accepted.
     */
    idToken: GoogleIdTokenSchema.optional(),
    consent: SIGN_UP_CONSENT,
})
    .openapi('SocialSignInRequest', { description: 'Nothing else is accepted: an unexpected field is refused.' });
export const SocialSignInSchema = z
    .object({
    /** `true` when the browser is being sent to the provider. */
    redirect: z.boolean(),
    /** The provider's sign-in URL, to send the player to. Absent with `idToken`. */
    url: z.string().optional(),
    /** With `idToken`: the session token, for `Authorization: Bearer`. */
    token: z.string().optional(),
    /** With `idToken`: who signed in. */
    user: AuthUserSchema.optional(),
})
    .openapi('SocialSignIn', {
    description: "The provider's URL to send the player to — or, with a Google ID token, a session.",
});
export const socialSignInRoute = createRoute({
    method: 'post',
    path: '/v1/auth/sign-in/social',
    operationId: 'socialSignIn',
    tags: IDENTITY,
    summary: 'Sign in, or sign up, with Google or Discord',
    description: 'The browser flow: the answer is the provider’s URL, and the provider sends the player back to ' +
        '`/v1/auth/callback/{provider}`. The phone apps’ Google flow: send the SDK’s `idToken` and get a session back. ' +
        'Send `consent` whenever the player may be new. No account is ever joined to another by email: an address ' +
        'that already has an account is refused (`account_not_linked`), and the provider is linked from that ' +
        'account’s session (`/v1/auth/link-social`). Never started on a kiosk: a kiosk reaches it only through the station sign-in handoff (ADR-0051).',
    request: json(SocialSignInRequestSchema),
    responses: {
        200: jsonResponse(SocialSignInSchema, 'The provider’s URL, or — with an ID token — a session.'),
        400: authError('Refused: a field is not accepted, or a new player’s ID token came without the terms and privacy.', 'UNEXPECTED_FIELDS', 'CONSENT_REQUIRED', 'VALIDATION_ERROR'),
        401: authError('The ID token is not Google’s, not for this api or expired, or its address already has an account.', 'INVALID_TOKEN', 'OAUTH_LINK_ERROR'),
        403: authError('The account is banned from the Platform, or a callback URL is not one the api trusts.', 'BANNED_USER', 'INVALID_CALLBACK_URL'),
        404: authError('The provider is not configured on this server, or cannot take an ID token.', 'PROVIDER_NOT_FOUND', 'ID_TOKEN_NOT_SUPPORTED'),
        409: authError('The consent names a document version that is not the one in force.', 'CONSENT_VERSION_OUTDATED'),
        429: RATE_LIMITED,
    },
});
export const OAuthCallbackQuerySchema = z.object({
    /** The provider's authorisation code. */
    code: z.string().optional(),
    /** The state the flow was started with. */
    state: z.string().optional(),
    /** The provider's refusal, when the player said no. */
    error: z.string().optional(),
    error_description: z.string().optional(),
});
/** A browser redirect: nothing to parse, only where it goes. */
const REDIRECT = {
    description: 'A redirect. Signed in: to `callbackURL` (or `newUserCallbackURL` for a new player) with the session cookie. ' +
        'Refused: to `errorCallbackURL` with `?error=<code>`, and no session.',
};
export const oauthCallbackRoute = createRoute({
    method: 'get',
    path: '/v1/auth/callback/{id}',
    // better-auth's document gives this endpoint no id, so it is its `auth.api` name (#412's rule).
    operationId: 'callbackOAuth',
    tags: IDENTITY,
    summary: 'Where Google or Discord sends the player back',
    description: 'Not called by a client: the provider redirects the browser here. The state must be the one this browser ' +
        'started with, unused and under 10 minutes old. A new player’s account, its profile and its consent rows are ' +
        'created here in one transaction, with the consent kept from `/v1/auth/sign-in/social`.',
    request: {
        params: z.object({ id: SocialSignInProviderSchema }),
        query: OAuthCallbackQuerySchema,
    },
    responses: { 302: REDIRECT },
});
export const LinkSocialAccountRequestSchema = z
    .object({
    provider: SocialSignInProviderSchema,
    callbackURL: CALLBACK_URL,
    errorCallbackURL: ERROR_CALLBACK_URL,
    disableRedirect: z.boolean().optional(),
})
    .openapi('LinkSocialAccountRequest', { description: 'Nothing else is accepted: an unexpected field is refused.' });
export const LinkSocialAccountSchema = z
    .object({
    /** The provider's sign-in URL, to send the player to. */
    url: z.string(),
    redirect: z.boolean(),
})
    .openapi('LinkSocialAccount');
export const linkSocialAccountRoute = createRoute({
    method: 'post',
    path: '/v1/auth/link-social',
    operationId: 'linkSocialAccount',
    tags: IDENTITY,
    summary: 'Link Google or Discord to the signed-in account',
    description: `${SESSION} The only way a provider joins an existing account ("Link Discord" in settings): the session proves ` +
        'the account and the round trip the identity, so the two addresses need not match. The provider sends the ' +
        'player back to `/v1/auth/callback/{provider}`; a provider account already linked to somebody else is refused.',
    request: json(LinkSocialAccountRequestSchema),
    responses: {
        200: jsonResponse(LinkSocialAccountSchema, 'The provider’s URL.'),
        400: authError('A field is not accepted.', 'UNEXPECTED_FIELDS', 'VALIDATION_ERROR'),
        401: authError('Not signed in.', 'UNAUTHORIZED'),
        404: authError('The provider is not configured on this server.', 'PROVIDER_NOT_FOUND'),
        429: RATE_LIMITED,
    },
});
export const LinkedAccountSchema = z
    .object({
    /** better-auth's account id: a UUID. */
    id: z.string(),
    /** `credential` for a password; otherwise the provider — `google`, `discord`, `steam`. */
    providerId: z.string(),
    /** The provider's id for the person: a Steam account's steamId64. */
    accountId: z.string(),
    userId: z.string(),
    scopes: z.array(z.string()),
    createdAt: IsoDateTimeSchema,
    updatedAt: IsoDateTimeSchema,
})
    .openapi('LinkedAccount', { description: 'A way into the account: its password, or a provider linked to it.' });
export const listLinkedAccountsRoute = createRoute({
    method: 'get',
    path: '/v1/auth/list-accounts',
    operationId: 'listUserAccounts',
    tags: IDENTITY,
    summary: 'The ways into the signed-in account',
    description: `${SESSION} Its password and every provider linked to it, for the security settings.`,
    responses: {
        200: jsonResponse(z.array(LinkedAccountSchema), 'Every way in.'),
        401: authError('Not signed in.', 'UNAUTHORIZED'),
        429: RATE_LIMITED,
    },
});
export const UnlinkAccountRequestSchema = z
    .object({
    /** The account row to unlink: a linked provider's `id` from `/v1/auth/list-accounts`, never the password's. */
    accountId: z.string().min(1),
})
    .openapi('UnlinkAccountRequest', { description: 'Nothing else is accepted: an unexpected field is refused.' });
export const UnlinkAccountSchema = z.object({ status: z.boolean() }).openapi('UnlinkAccount');
export const unlinkAccountRoute = createRoute({
    method: 'post',
    path: '/v1/auth/unlink-account',
    operationId: 'unlinkAccount',
    tags: IDENTITY,
    summary: 'Unlink Google, Discord or Steam from the signed-in account',
    description: `${SESSION} The session must be fresh: signed in within the last ten minutes (spec/07 §3.3). A provider is ` +
        'unlinked only while the account keeps another way in that this list counts: its password or another linked ' +
        'provider (`FAILED_TO_UNLINK_LAST_ACCOUNT` otherwise). A phone number or an email code is not counted, because ' +
        'either can be switched off or be a placeholder address. The password itself is never unlinked here.',
    request: json(UnlinkAccountRequestSchema),
    responses: {
        200: jsonResponse(UnlinkAccountSchema, 'Unlinked.'),
        400: authError('Refused: the last way in, the password, an account that is not the caller’s, or a field that is not accepted.', 'FAILED_TO_UNLINK_LAST_ACCOUNT', 'PASSWORD_NOT_UNLINKABLE', 'ACCOUNT_NOT_FOUND', 'UNEXPECTED_FIELDS', 'VALIDATION_ERROR'),
        401: authError('Not signed in.', 'UNAUTHORIZED'),
        403: authError('Signed in more than ten minutes ago: sign in again, then unlink.', 'SESSION_NOT_FRESH'),
        429: RATE_LIMITED,
    },
});
export const SteamStartRequestSchema = z
    .object({
    callbackURL: CALLBACK_URL,
    errorCallbackURL: ERROR_CALLBACK_URL,
    newUserCallbackURL: NEW_USER_CALLBACK_URL,
    consent: SIGN_UP_CONSENT,
    /**
     * `true` from a signed-in session: link the Steam account to the caller ("Link Steam" in settings)
     * rather than sign in. Steam is never linked by email.
     */
    link: z.boolean().optional(),
})
    .openapi('SteamStartRequest', { description: 'Nothing else is accepted: an unexpected field is refused.' });
export const SteamStartSchema = z
    .object({
    /** Steam's sign-in page, to send the player to. */
    url: z.string(),
})
    .openapi('SteamStart');
export const steamStartRoute = createRoute({
    method: 'post',
    path: '/v1/auth/steam/start',
    operationId: 'steamStart',
    tags: IDENTITY,
    summary: 'Sign in, or sign up, with Steam',
    description: 'The answer is Steam’s sign-in URL, whose `openid.return_to` is `/v1/auth/steam/callback` with a one-use state ' +
        'kept for 10 minutes and bound to this browser; Steam sends the player back there. Send `consent` whenever ' +
        'the player may be new. Never started on a kiosk: a kiosk reaches it only through the station sign-in handoff (ADR-0051).',
    request: json(SteamStartRequestSchema),
    responses: {
        200: jsonResponse(SteamStartSchema, 'Steam’s sign-in URL.'),
        400: authError('A field is not accepted, or the consent is not one that can be recorded.', 'UNEXPECTED_FIELDS', 'CONSENT_REQUIRED', 'VALIDATION_ERROR'),
        401: authError('`link` without a session.', 'UNAUTHORIZED'),
        403: authError('A callback URL is not one the api trusts.', 'INVALID_CALLBACK_URL'),
        404: authError('Steam sign-in is not configured on this server.', 'PROVIDER_NOT_FOUND'),
        409: authError('The consent names a document version that is not the one in force.', 'CONSENT_VERSION_OUTDATED'),
        429: RATE_LIMITED,
    },
});
export const steamCallbackRoute = createRoute({
    method: 'get',
    path: '/v1/auth/steam/callback',
    operationId: 'steamCallback',
    tags: IDENTITY,
    summary: 'Where Steam sends the player back',
    description: 'Not called by a client: Steam redirects the browser here with its OpenID 2.0 assertion (`openid.*`). Nothing ' +
        'is believed until the state is this browser’s and unused, the assertion is Steam’s for the exact URL issued, ' +
        'its nonce is unseen in 24 hours and Steam’s `check_authentication` confirms it (spec/07 §3.4). The account is ' +
        'found by its steamId64, or created with its consent in one transaction.',
    request: {
        query: z.object({
            /** The state `/v1/auth/steam/start` issued, carried in `openid.return_to`. */
            state: z.string().optional(),
        }),
    },
    responses: { 302: REDIRECT },
});
/** The link handoff's secret, as the station's sign-in window holds it (ADR-0053). */
export const StationLinkStartRequestSchema = z
    .object({
    /** The link handoff's secret from the page's fragment: 32 random bytes, base64url. */
    token: z.string().regex(/^[A-Za-z0-9_-]{43}$/, 'A handoff secret'),
})
    .strict()
    .openapi('StationLinkStartRequest', { description: 'Nothing else is accepted.' });
export const StationLinkStartSchema = z
    .object({
    /** The provider's own sign-in page, to send the window to at once. */
    url: z.string(),
})
    .openapi('StationLinkStart');
export const stationLinkStartRoute = createRoute({
    method: 'post',
    path: '/v1/auth/station-link/start',
    operationId: 'stationLinkStart',
    tags: IDENTITY,
    summary: 'Start linking a provider to the player on a PC, from the station’s sign-in window',
    description: 'No session: the link handoff’s secret (ADR-0053) is the authority, once. While the handoff is waiting, unexpired, ' +
        'and the session it was made for is still running on that PC for that player, starts the provider’s link flow for ' +
        'exactly that player and answers the provider’s URL; the round trip comes back to `/v1/auth/callback/{provider}` ' +
        '(Steam: `/v1/auth/steam/callback`) and then to the player web’s `/station-link`. It makes no session and can do ' +
        'nothing but link that one provider to that one account. 30 a minute per address.',
    request: json(StationLinkStartRequestSchema),
    responses: {
        200: jsonResponse(StationLinkStartSchema, 'The provider’s URL.'),
        400: authError('A field is not accepted.', 'VALIDATION_ERROR'),
        404: authError('The provider is not configured on this server.', 'PROVIDER_NOT_FOUND'),
        409: authError('The link is unknown, past its time, cancelled, already used, or its session has ended.', 'LINK_HANDOFF_UNAVAILABLE'),
        429: RATE_LIMITED,
    },
});
/** Every route here, in the order a client meets them. */
export const AUTH_ROUTES = [
    sendPhoneCodeRoute,
    verifyPhoneNumberRoute,
    requestPasswordResetByPhoneRoute,
    resetPasswordWithPhoneCodeRoute,
    signUpWithEmailRoute,
    sendEmailCodeRoute,
    verifyEmailWithCodeRoute,
    signInWithEmailRoute,
    signInWithUsernameRoute,
    signInWithEmailCodeRoute,
    requestPasswordResetByEmailRoute,
    resetPasswordWithEmailCodeRoute,
    checkUsernameAvailabilityRoute,
    updateUserRoute,
    socialSignInRoute,
    oauthCallbackRoute,
    steamStartRoute,
    steamCallbackRoute,
    linkSocialAccountRoute,
    stationLinkStartRoute,
    listLinkedAccountsRoute,
    unlinkAccountRoute,
    getSessionRoute,
    signOutRoute,
];
//# sourceMappingURL=auth.js.map